import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from '@ott/button';
import { CreditCard, CREDIT_CARD_FIELDS_NAMES as CreditCardFields } from '@ott/credit-card';
import FirstErrorShower from '@ott/first-error-shower';
import Input from '@ott/input';
import { l10n } from '@ott/l10n';
import PaymentVariants from '@ott/payment-variants';
import { hooks, actions as virtualCardActions } from '@ott/shared-virtual-card-logic';

import { unwrapResult } from '@reduxjs/toolkit';

import { IframeDataResponseType, RefillFormData } from '../../types';
import ExpandableNote from '../ExpandableNote';
import { parseAmount, formatAmount } from './helpers/amount';
import { FORM_FIELDS, MANUAL_TYPE, VISIBLE_CARDS } from './helpers/constants';
import { getErrorByCode } from './helpers/error';
import { getVariants, prepareData } from './helpers/helpers';
import { prepareRefillCards } from './helpers/prepareRefillCards';
import { useRefillForm } from './hooks/useRefillForm';
import { RefillFormType } from './types';

import styles from './RefillForm.scss';

const {
  virtualCardFlow: { useVirtualCardFlow },
} = hooks;

type Props = {
  redirect: (iframeData: IframeDataResponseType, formData: RefillFormData) => void;
  amount: string;
};

const RefillForm = ({ redirect, amount }: Props) => {
  const formRef = useRef<HTMLFormElement | null>(null);

  const [canRefocus, setCanRefocus] = useState(false);
  const [shouldShowAllCards, setShouldShowAllCards] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const dispatch = useDispatch<any>();

  const {
    auth,
    auth: { cards = [], isLoading: isUserInfoLoading },
  } = useVirtualCardFlow();

  const refillCards = useMemo(() => prepareRefillCards(cards), [cards]);

  const { config } = useVirtualCardFlow();

  const {
    form: {
      control,
      formState: { isSubmitted },
      setValue,
      handleSubmit,
    },
    preparedCard,
    setActiveCard,
    cardId,
    preapredErrors,
    submitTime,
  } = useRefillForm({ amount: Number(amount) });

  // If got cards - setting first selected
  useEffect(() => {
    if (refillCards.length && !canRefocus) {
      setValue(FORM_FIELDS.selectedId, refillCards[0].cardId);
      setCanRefocus(true);
    }
  }, [refillCards]);

  const handleError = (err: string) => {
    const errorMessage = getErrorByCode(err);
    setSubmitError(errorMessage);
  };

  const handleRefill = async (data: RefillFormType) => {
    setIsLoading(true);

    const preparedData = prepareData(data);

    dispatch(virtualCardActions.refillCard(preparedData))
      .then(unwrapResult)
      .then((result: { postParams: IframeDataResponseType } | IframeDataResponseType) => {
        if ('postParams' in result) {
          redirect(result.postParams, preparedData);
        } else {
          redirect(result, preparedData);
        }
      })
      .catch(handleError)
      .finally(async () => {
        // If new card - getting user info (includes saved cards)
        if (!data[FORM_FIELDS.id] && data[FORM_FIELDS.saveCard]) {
          await auth.getBasicUserInfo();
        }
        setIsLoading(false);
      });
  };

  const handleVariantChange = (value: string | number | boolean) => {
    if (typeof value === 'string') {
      setValue(FORM_FIELDS.selectedId, value);
      setValue(FORM_FIELDS.cvv, '');
    }
  };

  const variants = useMemo(() => {
    return getVariants(refillCards, cardId ?? '', isLoading, control).slice(
      0,
      shouldShowAllCards ? refillCards.length : VISIBLE_CARDS
    );
  }, [shouldShowAllCards, cardId, isLoading, control]);

  const { refillMessages = [] } = config || {};

  const creditCardErrors = {
    [CreditCardFields.cardHolder]: preapredErrors[CreditCardFields.cardHolder],
    [CreditCardFields.cardNumber]: preapredErrors[CreditCardFields.cardNumber],
    [CreditCardFields.cvv]: preapredErrors[CreditCardFields.cardNumber],
    [CreditCardFields.month]: preapredErrors[CreditCardFields.month],
    [CreditCardFields.year]: preapredErrors[CreditCardFields.year],
    [CreditCardFields.saveCard]: preapredErrors[CreditCardFields.saveCard],
  };

  return (
    <form
      className={styles.form}
      ref={formRef}
      data-locator="refill-form"
      onSubmit={handleSubmit(handleRefill)}
    >
      <FirstErrorShower
        useRHFErrors={true}
        errors={preapredErrors}
        time={submitTime}
        container={formRef.current}
        canScrollWindow={false}
        withFocus={true}
        show={isSubmitted}
      />

      {refillMessages?.length > 0 &&
        refillMessages.map(({ text }) => <ExpandableNote key={text} text={text} />)}

      <PaymentVariants
        value={cardId ?? ''}
        onChange={handleVariantChange}
        variants={variants}
        className={styles.paymentVariants}
        allowSingleVaraint={true}
      />

      {refillCards.length > VISIBLE_CARDS && (
        <button
          type="button"
          className={styles.allCardsButton}
          onClick={() => setShouldShowAllCards(!shouldShowAllCards)}
          data-locator="refill-form-expand-collapse-saved-cards"
        >
          {!shouldShowAllCards
            ? l10n('virtualCard.refillForm.expandCardList')
            : l10n('virtualCard.refillForm.collapseCardList')}
        </button>
      )}

      {/* Отдельный PaymentVariants для варианта "Новая карта" */}
      <PaymentVariants
        value={cardId ?? ''}
        onChange={handleVariantChange}
        variants={[
          {
            value: MANUAL_TYPE,
            title: l10n('virtualCard.refillForm.fields.newCardVariant'),
            icons: ['default'],
            disabled: isLoading,
          },
        ]}
        allowSingleVaraint={Boolean(variants.length)}
        className={styles.paymentVariants}
      />
      {!cardId && (
        <CreditCard
          className={styles.creditCard}
          activeCard={preparedCard}
          setActiveCard={setActiveCard}
          savedCards={[]}
          errors={creditCardErrors}
          isSubmitted={isSubmitted}
        />
      )}

      <Controller
        control={control}
        name={FORM_FIELDS.amount}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            hasError={(isSubmitted || fieldState.isTouched) && fieldState.invalid}
            placeholder={l10n('virtualCard.refillForm.fields.amount')}
            className={styles.amount}
            specificLocators="refill-amount-input"
            parser={parseAmount}
            formatter={formatAmount}
          />
        )}
      />

      <p className={styles.details}>{l10n('virtualCard.refillForm.details')}</p>

      <Button
        type="submit"
        isLoading={isLoading || isUserInfoLoading}
        withFullWidth={true}
        specificLocators="refill-submit"
      >
        {l10n('virtualCard.refillForm.button')}
      </Button>

      {submitError && (
        <p className={styles.error} data-locator="refill-form-error-message">
          {submitError}
        </p>
      )}
    </form>
  );
};

export default React.memo(RefillForm);
